import { NotificationType, useApp, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { Button, ButtonSize, ButtonVariant, IconVariants, Modal, Shape } from "@next-platform/client-common-spicy-ui";
import { useState } from "react";

import useApi from "@/components/useApi";
import { NSFW_TOGGLE_LOCALSTORAGE_KEY } from "@/constants";
import { useRecombee } from "@/hooks";
import useToast from "@/hooks/useToast";
import { removeRecommendationFromLocalStorage } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import { CharacterRating as CharacterRatingEnum, CharacterRatingWeights, MAX_CHARACTER_RATING } from "./constants";

type RadioOptions = {
  value: number;
  icon: IconVariants;
  selectedClassName?: string;
  classNames?: string;
};

type CharacterRatingProps = {
  characterId?: string;
  conversationLength: number;
  open: boolean;
  onClose: () => void;
};

function CharacterRating({ characterId, conversationLength, open, onClose }: CharacterRatingProps) {
  const { t } = useTranslation();
  const { recombeeRecommendationsConfig, isNsfwEnabled } = useApp();
  const {
    homepageRecommendationsFetchCount,
    homepageRecommendationsDislikedAllowedToRemoveCount,
    recommendationspageFetchCount,
    recommendationspageDislikedAllowedToRemoveCount,
  } = recombeeRecommendationsConfig ?? {};
  const { sendUserInteractionRating } = useRecombee();
  const { userId } = useUserInfo();
  const { callApi, jsonApiService } = useApi();
  const { onUserSetCharacterRating, characterRatings: data } = useUserSettings();
  const characterRatings = data as Record<string, number> | undefined;
  const previousRating = characterRatings?.[characterId ?? ""] ?? null;
  const [ratingValue, setRatingValue] = useState(previousRating);
  const [loading, setLoading] = useState(false);
  const { displayToast } = useToast();
  const isNsfwToggleEnabled = !!(isNsfwEnabled && typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY) === "true");

  const handleVote = async (value: number) => {
    setLoading(true);

    const promises = [];

    //send events if value has changed
    if (previousRating !== value) {
      promises.push(
        callApi(jsonApiService.createResource, "event", {
          event_name: "characterRating",
          character_id: characterId,
          rating: value,
          conversation_length: conversationLength,
        }),
      );

      let recomValue: number = 0;

      switch (value) {
        case CharacterRatingEnum.DISLIKE:
          recomValue = CharacterRatingWeights.DISLIKE;
          removeRecommendationFromLocalStorage({
            characterId: characterId as string,
            nsfwToggle: isNsfwToggleEnabled,
            homepageRecommendationsFetchCount,
            homepageRecommendationsDislikedAllowedToRemoveCount,
            recommendationspageFetchCount,
            recommendationspageDislikedAllowedToRemoveCount,
          });
          break;
        case CharacterRatingEnum.LIKE:
          recomValue = CharacterRatingWeights.LIKE;
          break;
        case CharacterRatingEnum.LOVE:
          recomValue = CharacterRatingWeights.LOVE;
          break;
        default:
          setLoading(false);
          onClose();
          return;
      }

      promises.push(sendUserInteractionRating(userId + "", characterId + "", recomValue));
    }

    try {
      const payload = { characterId: characterId as string, rating: value };
      onUserSetCharacterRating(payload);

      await Promise.all(promises);

      onClose();
      setLoading(false);
      displayToast({ message: t("chat:page.characterRating.toast.success"), type: NotificationType.success });
    } catch {
      onClose();
      setLoading(false);
      displayToast({ message: t("chat:page.characterRating.toast.error"), type: NotificationType.error });
    }
  };

  const rateModalActionButtons = [
    {
      label: t("common:cancel"),
      onPress: () => onClose(),
      variant: ButtonVariant.TEXT,
      size: ButtonSize.LARGE,
    },
    {
      label: t("common:done"),
      onPress: () => {
        handleVote(ratingValue as number);
      },
      variant: ButtonVariant.CONTAINED,
      size: ButtonSize.LARGE,
      disabled: previousRating === ratingValue || !ratingValue,
      loading: loading,
    },
  ];

  const ratingOptions: RadioOptions[] = [
    {
      value: CharacterRatingEnum.DISLIKE,
      icon: IconVariants.DISLIKE,
      selectedClassName: "bg-red-9 active:bg-red-9 active:border-red-9",
      classNames: "hover:bg-red-9 hover:border-red-9",
    },
    {
      value: CharacterRatingEnum.LIKE,
      icon: IconVariants.LIKE,
      selectedClassName: "bg-blue-9 active:bg-blue-9 active:border-blue-9",
      classNames: "hover:bg-blue-9 hover:border-blue-9",
    },
    {
      value: CharacterRatingEnum.LOVE,
      icon: IconVariants.LIKE,
      selectedClassName: "bg-purple-9 active:bg-purple-9 active:border-purple-9",
      classNames: "hover:bg-purple-9 hover:border-purple-9",
    },
  ];

  return (
    <Modal
      title={t("chat:page.characterRating.title")}
      description={t("chat:page.characterRating.description")}
      open={open}
      actions={rateModalActionButtons}
      onOpenChange={onClose}
    >
      <div className="flex justify-between gap-md">
        {ratingOptions.map((ratingOptionsItem: RadioOptions) => {
          const isSelected = ratingValue === ratingOptionsItem.value;
          return (
            <Button
              key={ratingOptionsItem?.value}
              className={`flex-1 hover:text-white ${isSelected ? ratingOptionsItem?.selectedClassName : " "} ${ratingOptionsItem.classNames}`}
              iconStart={ratingOptionsItem.icon}
              iconEnd={ratingOptionsItem.value === MAX_CHARACTER_RATING ? ratingOptionsItem.icon : undefined}
              onClick={() => setRatingValue(ratingOptionsItem.value)}
              shape={Shape.SQUARE}
              size={ButtonSize.LARGE}
              variant={isSelected ? ButtonVariant.CONTAINED : ButtonVariant.OUTLINED}
            />
          );
        })}
      </div>
    </Modal>
  );
}

export default CharacterRating;
